/**
 * IdP form validation logic.
 *
 * Validates metadata URL, client ID, and client secret fields
 * for the Identity Provider configuration form.
 */

export interface AttributeMapping {
  readonly sourceClaim: string;
  readonly targetProperty: 'email' | 'displayName' | 'groups' | 'role';
}

export interface IdpFormState {
  readonly type: 'oidc' | 'saml';
  readonly metadataUrl: string;
  readonly clientId: string;
  readonly clientSecret: string;
  readonly attributeMappings: AttributeMapping[];
}

export interface IdpFormErrors {
  metadataUrl?: string;
  clientId?: string;
  clientSecret?: string;
}

const MAX_METADATA_URL_LENGTH = 2048;
const MAX_CLIENT_ID_LENGTH = 256;
const MAX_CLIENT_SECRET_LENGTH = 512;

/**
 * Validates an IdP form state and returns field-level errors.
 *
 * Rules:
 * - metadataUrl: error if empty, does not start with 'https://', or exceeds 2048 chars
 * - clientId: error if empty or exceeds 256 chars
 * - clientSecret: error if empty or exceeds 512 chars
 *
 * Returns an empty object when all fields are valid.
 */
export function validateIdpForm(state: IdpFormState): IdpFormErrors {
  const errors: IdpFormErrors = {};

  if (!state.metadataUrl || state.metadataUrl.trim().length === 0) {
    errors.metadataUrl = 'Metadata URL is required';
  } else if (!state.metadataUrl.startsWith('https://')) {
    errors.metadataUrl = 'Metadata URL must start with https://';
  } else if (state.metadataUrl.length > MAX_METADATA_URL_LENGTH) {
    errors.metadataUrl = `Metadata URL must not exceed ${MAX_METADATA_URL_LENGTH} characters`;
  }

  if (!state.clientId || state.clientId.trim().length === 0) {
    errors.clientId = 'Client ID is required';
  } else if (state.clientId.length > MAX_CLIENT_ID_LENGTH) {
    errors.clientId = `Client ID must not exceed ${MAX_CLIENT_ID_LENGTH} characters`;
  }

  if (!state.clientSecret || state.clientSecret.trim().length === 0) {
    errors.clientSecret = 'Client secret is required';
  } else if (state.clientSecret.length > MAX_CLIENT_SECRET_LENGTH) {
    errors.clientSecret = `Client secret must not exceed ${MAX_CLIENT_SECRET_LENGTH} characters`;
  }

  return errors;
}

/**
 * Returns true if the form has no validation errors.
 */
export function isIdpFormValid(state: IdpFormState): boolean {
  const errors = validateIdpForm(state);
  return Object.keys(errors).length === 0;
}
